// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, Fade, FormControl, FormGroup, InputLabel, ListSubheader, MenuItem, Select, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { CreateNewCustomBundleThunk, GetAllCustomBundlesThunk, GetCustomBundleParentsThunk, GetDeliverablesByBundleThunk } from 'Features/bundle/bundleThunk';
import AddOnItem from './AddOnItem';
import stylesDef from './styles';

// The old parents keep the toggles they always had, keyed on their names. A catalog product's follow its add-ons' own
// fields: 3D where an add-on has a 3D variant, the Output choice where its add-ons carry an attribute factor.
const LEGACY_3D_PARENT = 'Underground A-la-carte';
const LEGACY_ATTRIBUTE_PARENT = 'Aerial A-la-carte';
const OFF_CATALOG_WARNING = "This account isn't on the product catalog yet. A bundle built on a product shows to the customer once it is.";

interface CreateCustomBundleDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

// The picked parent, by id, and whether it is a catalog product
const findParent = (parents: ICustomBundleParents | null, id: string) => {
  const group = parents?.groups.find(({ options }) => options.some(({ _id }) => _id === id));
  const option = group?.options.find(({ _id }) => _id === id);
  return group && option ? { option, isProduct: group.key === 'products' } : null;
};

const CreateCustomBundleDialog = ({ open, setOpen }: CreateCustomBundleDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const _id = _ADMIN_ ? useAppSelector((state) => state.admin.orgId) : useAppSelector((state) => state.account.org._id);

  const [parents, setParents] = useState<ICustomBundleParents | null>(null);
  const [customBundleName, setCustomBundleName] = useState<string>('');
  const [parentId, setParentId] = useState<string>('');
  const [bundleData, setBundleData] = useState<BundleData | null>(null);
  const [layersLoading, setLayersLoading] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState<{ _id: string, is3D: boolean }[]>([]);
  const [pointsAndAttributes, setPointsAndAttributes] = useState<boolean>(false);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notice, setNotice] = useState('');

  const picked = findParent(parents, parentId);
  const isProduct = !!picked?.isProduct;
  const parentName = picked?.option.name ?? '';

  const onClose = () => {
    setCustomBundleName('');
    setParentId('');
    setBundleData(null);
    setAllSelected(false);
    setPointsAndAttributes(false);
    setSelectedAddons([]);
    setNotice('');
    setOpen(false);
  };

  const onSubmit = async () => {
    if (customBundleName === '' || !bundleData) {
      return;
    }
    setIsLoading(true);
    setNotice('');
    const deliverablesList = bundleData.defaultDeliverables.map((del) => ({ _id: del.id, is3D: false }));
    const result = await dispatch(CreateNewCustomBundleThunk(customBundleName, selectedAddons, deliverablesList, bundleData.parentId, pointsAndAttributes));
    if (result.success) {
      await dispatch(GetAllCustomBundlesThunk(_id));
      onClose();
    } else {
      setNotice(result.message);
    }
    setIsLoading(false);
  };

  const handleAddonCheck = (event: React.ChangeEvent<HTMLInputElement>, id: string, is3D?: boolean) => {
    if (event.target.checked) {
      setSelectedAddons((prev) => [...prev, { _id: id, is3D: is3D || false }]);
    } else {
      setSelectedAddons((prev) => prev.filter((addon) => addon._id !== id));
    }
  };

  // The parents are read each time the dialog opens, for the organization it is opened on
  useEffect(() => {
    if (!open || !_id) {
      return undefined;
    }
    let current = true;
    const fetchParents = async () => {
      const result = await dispatch(GetCustomBundleParentsThunk(_id));
      if (current) {
        setParents(result);
      }
    };
    fetchParents();
    return () => {
      current = false;
    };
  }, [open, _id]);

  // A parent's layers paint only once they have loaded; a pick made since then wins over a late answer
  useEffect(() => {
    setSelectedAddons([]);
    setBundleData(null);
    if (parentId === '') {
      setLayersLoading(false);
      return undefined;
    }
    let current = true;
    setLayersLoading(true);
    const fetchDeliverables = async () => {
      const result = await dispatch(GetDeliverablesByBundleThunk(parentId));
      if (current) {
        setBundleData(result || null);
        setLayersLoading(false);
        setNotice(result ? '' : "Couldn't load this parent's layers. Choose it again to retry.");
      }
    };
    fetchDeliverables();
    return () => {
      current = false;
    };
  }, [parentId]);

  useEffect(() => {
    if (bundleData) {
      const { attributeAddOnsList } = bundleData;
      setAllSelected(attributeAddOnsList.length > 0 && attributeAddOnsList.every((item) => selectedAddons.some((selected) => selected._id === item)));
    }
  }, [selectedAddons]);

  const parentItems = (parents?.groups ?? []).flatMap((group) => [
    <ListSubheader key={group.key}>{group.label}</ListSubheader>,
    ...group.options.map((option) => (
      <MenuItem key={option._id} value={option._id}>
        {option.name}
      </MenuItem>
    )),
  ]);
  const showOutput = allSelected && (isProduct || parentName === LEGACY_ATTRIBUTE_PARENT);

  return (
    <Dialog open={open} onClose={onClose}>
      <Box sx={[styles.dialogContainer, !!bundleData && styles.dialogContainerWithData]}>
        <Typography sx={styles.dialogContainerTitle}>Create Custom Bundle</Typography>
        <Typography>Choose a parent product and the layers to draft into this bundle.</Typography>
        <Box sx={styles.dialogInputs}>
          <TextField
            label="Name"
            sx={styles.inputWidth}
            variant="standard"
            placeholder="Custom Bundle Name"
            value={customBundleName}
            onChange={(event) => setCustomBundleName(event.target.value)}
          />
          <FormControl sx={styles.inputWidth} variant="standard">
            <InputLabel id="custom-bundle-parent-label">Parent product</InputLabel>
            <Select
              labelId="custom-bundle-parent-label"
              variant="standard"
              value={parentId}
              onChange={(event) => setParentId(event.target.value)}
              disabled={!parents}
            >
              {parentItems}
            </Select>
          </FormControl>
        </Box>
        {isProduct && parents && !parents.catalogEnabled && <Alert severity="warning">{OFF_CATALOG_WARNING}</Alert>}
        <Box sx={styles.deliverablesContainer}>
          {layersLoading ? (
            <Box sx={styles.layersLoading}>
              <CircularProgress aria-label="Loading layers" />
            </Box>
          ) : bundleData?.additionalDeliverables.map((item) => (
            <FormGroup key={item.subtype} sx={styles.subtypeContainer}>
              <Typography variant="subtitle1" sx={styles.subtypeName}>{item.subtype}</Typography>
              {item.deliverables.map((del) => (
                <AddOnItem
                  key={del.id}
                  item={del}
                  handleAddonCheck={handleAddonCheck}
                  selectedAddons={selectedAddons}
                  available3D={del.has3D && (isProduct || parentName === LEGACY_3D_PARENT)}
                />
              ))}
            </FormGroup>
          ))}
        </Box>
        {notice && <Alert severity="error">{notice}</Alert>}
        <Box sx={allSelected ? styles.buttonContainerWithAttributes : styles.buttonContainer}>
          {showOutput ? (
            <Box sx={styles.pointsAndAttributesContainer}>
              <Typography sx={styles.outputText}>
                Output:
              </Typography>
              <ToggleButtonGroup
                value={pointsAndAttributes}
                exclusive
                sx={styles.pointsToggle}
                onChange={(_event, value) => {
                  setPointsAndAttributes(value);
                }}
                aria-label="attribute toggle"
              >
                <ToggleButton
                  value={false}
                  sx={!pointsAndAttributes ? styles.selected : styles.notSelected}
                  disabled={!pointsAndAttributes}
                >
                  Location
                </ToggleButton>
                <ToggleButton
                  value
                  sx={pointsAndAttributes ? styles.selected : styles.notSelected}
                  disabled={pointsAndAttributes}
                >
                  Location & Dimension
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : null}
          <Box sx={styles.buttonGroup}>
            <Button sx={styles.closeButton} onClick={onClose}>Close</Button>
            <Button
              color="primary"
              variant="contained"
              onClick={onSubmit}
              disabled={customBundleName === '' || !bundleData || isLoading}
            >
              <Fade in={isLoading}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!isLoading}>
                <span>Save Changes</span>
              </Fade>
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateCustomBundleDialog;
